import { mutate } from 'swr';

import { clearDataFileContentCache } from '../features/dataFiles';
import { clearAllGoProUploadState } from './goproUploadState';
import { clearCircuitDiscoveryCache, setCircuitDiscoveryCacheScope } from './circuitDiscoveryCache';
import {
  cancelRegisteredAuthenticatedRequests,
  KIOSK_ACCOUNT_TERMINATION_STORAGE_KEY,
  KIOSK_BROWSER_LEASE_STORAGE_KEY,
} from './kioskAccountSession';
import { KIOSK_CLEAR_LOCK_STORAGE_KEY } from '../pages/kiosk/kioskClearLifecycle';
import { resetQueryCache } from './queryClient';
import { invalidateSessionListCache } from './sessionListCache';
import { invalidateCircuitOutlineCache } from './circuitOutlineCache';
import { clearGlobalSearch } from '../features/globalSearch/invalidation';

const JOIN_REQUEST_SENT_KEY = 'gridline:joinRequestSent';
const TRACE_CHANNEL_SELECTION_STORAGE_PREFIX = 'gridline:telemetry:trace-channels';
const KIOSK_STATION_CREDENTIAL_STORAGE_KEY = 'paddiq:kiosk-credential';

function clearTraceChannelSelections() {
  if (typeof window === 'undefined') {
    return;
  }

  const keysToRemove: string[] = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (key?.startsWith(TRACE_CHANNEL_SELECTION_STORAGE_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => window.localStorage.removeItem(key));
}

function clearKioskCustomerStorage() {
  if (typeof window === 'undefined') return;
  const preservedLocalKeys = new Set([
    KIOSK_STATION_CREDENTIAL_STORAGE_KEY,
    KIOSK_BROWSER_LEASE_STORAGE_KEY,
    KIOSK_ACCOUNT_TERMINATION_STORAGE_KEY,
    KIOSK_CLEAR_LOCK_STORAGE_KEY,
  ]);
  const keysToRemove: string[] = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (key && !preservedLocalKeys.has(key)) keysToRemove.push(key);
  }
  keysToRemove.forEach((key) => window.localStorage.removeItem(key));
  window.sessionStorage.clear();
}

async function clearBrowserCacheStorage() {
  if (typeof window === 'undefined' || !('caches' in window)) return;
  const keys = await window.caches.keys();
  await Promise.all(keys.map((key) => window.caches.delete(key)));
}

/** Drop cached API and persisted session data when auth changes. */
export async function resetSessionState(userId?: string | null, options: { kiosk?: boolean } = {}) {
  cancelRegisteredAuthenticatedRequests();
  clearGlobalSearch();
  resetQueryCache();
  mutate(() => true, undefined, { revalidate: false });
  const dataFileCleanup = clearDataFileContentCache();
  invalidateSessionListCache();
  invalidateCircuitOutlineCache();
  clearCircuitDiscoveryCache(userId);
  setCircuitDiscoveryCacheScope(null);

  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(JOIN_REQUEST_SENT_KEY);
    clearTraceChannelSelections();
  }

  if (options.kiosk) clearKioskCustomerStorage();

  const cleanup = [dataFileCleanup];
  if (options.kiosk) {
    cleanup.push(clearAllGoProUploadState(), clearBrowserCacheStorage());
  }
  const results = await Promise.allSettled(cleanup);
  const failure = results.find(
    (result): result is PromiseRejectedResult => result.status === 'rejected',
  );
  if (failure) throw failure.reason;
}
